import { createContext, useContext, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarHeart, Volume2, VolumeX, Type, Compass } from "lucide-react";
import { useSound } from "@/lib/SoundContext";
import { track } from "@/lib/analytics";
import CompanionCat from "./CompanionCat";
import ConciergeModal from "./ConciergeModal";
import DemandeModal from "./DemandeModal";

const RdvContext = createContext(null);
export const useRdv = () => useContext(RdvContext);

export default function SiteExtras({ children }) {
  const { muted, toggleMuted } = useSound();
  const location = useLocation();
  const navigate = useNavigate();
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [demande, setDemande] = useState({ open: false, type: null });
  const [reading, setReading] = useState(() => localStorage.getItem("dl_reading") === "1");

  useEffect(() => {
    document.documentElement.classList.toggle("dl-reading", reading);
    localStorage.setItem("dl_reading", reading ? "1" : "0");
  }, [reading]);

  // Mesure de visite (page_view) à chaque changement de page.
  useEffect(() => { track("page_view"); }, [location.pathname]);

  const openRdv = () => { track("rdv_open"); navigate("/rendez-vous"); };
  const openConcierge = () => { track("concierge_open"); setConciergeOpen(true); };
  const openDemande = (type = null) => setDemande({ open: true, type });

  return (
    <RdvContext.Provider value={{ openRdv, openConcierge, openDemande }}>
      {children}
      <CompanionCat />

      {/* Floating controls */}
      <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
        <div className="flex gap-2">
          <button
            data-testid="reading-toggle"
            onClick={() => setReading((r) => !r)}
            aria-label="Lecture confortable"
            title="Lecture confortable"
            className={`w-11 h-11 flex items-center justify-center border transition-colors backdrop-blur ${reading ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.35)] bg-[#0B1020]/90 text-[#C6A76A] hover:bg-[#C6A76A] hover:text-[#050814]"}`}
          >
            <Type size={18} />
          </button>
          <button
            data-testid="sound-toggle"
            onClick={toggleMuted}
            aria-label={muted ? "Activer le son" : "Couper le son"}
            className="w-11 h-11 flex items-center justify-center border border-[rgba(198,167,106,0.35)] bg-[#0B1020]/90 text-[#C6A76A] hover:bg-[#C6A76A] hover:text-[#050814] transition-colors backdrop-blur"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        </div>
        <button
          data-testid="concierge-open-btn"
          onClick={openConcierge}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[rgba(198,167,106,0.45)] bg-[#0B1020]/90 text-[#D4B978] text-[0.58rem] tracking-widest uppercase font-semibold hover:bg-white/[0.05] transition-colors backdrop-blur"
        >
          <Compass size={12} /> Que recherchez-vous ?
        </button>
        <button
          data-testid="rdv-open-btn"
          onClick={openRdv}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#C6A76A] text-[#050814] text-[0.6rem] tracking-widest uppercase font-bold hover:bg-[#D4B978] transition-colors shadow-lg"
        >
          <CalendarHeart size={13} /> Prendre rendez-vous
        </button>
      </div>

      <ConciergeModal open={conciergeOpen} onClose={() => setConciergeOpen(false)} openDemande={openDemande} />
      <DemandeModal open={demande.open} presetType={demande.type} onClose={() => setDemande({ open: false, type: null })} />
    </RdvContext.Provider>
  );
}
